import React from 'react';

import {
  deleteTargetingPreset,
  ISimpleTargetingPreset,
  TargetingPresetListViewAPIKey,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import {
  DateTimeRender,
  EllipsisWithTooltipRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { Div, Text } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteTargetingPresetClone,
  RouteTargetingPresetExisting,
} from '~/src/dash/router/routes';

export const formatTargetingPresetResponseIntoLVTRows = (
  presets: Record<string, any>[],
  cfg: ITargetingPresetListViewTable,
): IListViewTableRow[] => {
  return presets.map((preset) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = preset[responseKey];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    return formattedRow;
  });
};

const getTargetingPresetSettingsRoute =
  (session: ISessionState, user: IUserState) => (id: string) =>
    getRoutePath(RouteTargetingPresetExisting, extractRouteParams({ session, user }), {
      targetingpresetid: id,
    });

const getHandleClonePreset = (session: ISessionState, user: IUserState) => (id: string) => {
  navigate(RouteTargetingPresetClone, extractRouteParams({ session, user }), {
    presetid: id,
  });
  return;
};

const BiddersListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnAssociatedBidders(props.row.ID.value)}
      style={{ cursor: value ? 'pointer' : 'default' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

const CampaignsListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnAssociatedCampaigns(props.row.ID.value)}
      style={{ cursor: value ? 'pointer' : 'default' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

type TargetingPresetAssociatedItemKey = 'bidder' | 'campaign';

export interface ITargetingPresetAssociatedItem {
  title: string;
  dataKey: keyof ISimpleTargetingPreset;
}

interface ITargetingPresetListViewTable {
  columns: IListViewTableColumns;
}
export interface ITargetingPresetListViewConfig {
  cypressClassName: string;
  defaultState: {
    order: Order;
    orderBy: string;
    page: number;
    activeColumnKeys: TargetingPresetListViewAPIKey[];
    rowsPerPage: RowsPerPage;
  };
  isInitialized?: boolean;
  getSettingsRouteFn: (session: ISessionState, user: IUserState) => (id: string) => string;
  getHandleClone: (session: ISessionState, user: IUserState) => (id: string) => void;
  deletePresetFn: (publisherID: string, presetID: string) => Promise<any>;
  customDeleteMessage: string;
  table: ITargetingPresetListViewTable;
  dialogs: Record<TargetingPresetAssociatedItemKey, ITargetingPresetAssociatedItem>;
}

export const targetingPresetListViewConfig: ITargetingPresetListViewConfig = {
  isInitialized: false,
  defaultState: {
    order: 'desc',
    orderBy: 'UpdatedAt',
    page: 0,
    activeColumnKeys: ['NumBidders', 'NumCampaigns', 'UpdatedAt'],
    rowsPerPage: 10,
  },
  getSettingsRouteFn: getTargetingPresetSettingsRoute,
  getHandleClone: getHandleClonePreset,
  deletePresetFn: deleteTargetingPreset,
  customDeleteMessage:
    'Deleting a targeting preset deactivates associated bidders/campaigns and removes the preset from bidder/campaign targeting. To edit a bidder/campaign go to its settings, toggle off the preset, and add desired parameters.',
  cypressClassName: 'targeting-preset-listview',
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
        orderByKey: 'Name',
      },
      NumBidders: {
        cellJSX: BiddersListModalLink,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'NumBidders',
        label: 'Bidders Associated',
        requestKey: 'NumBidders',
        responseKey: 'NumBidders',
        valueType: 'number',
        defaultOrderDirection: 'desc',
      },
      NumCampaigns: {
        cellJSX: CampaignsListModalLink,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'NumCampaigns',
        label: 'Campaigns Associated',
        requestKey: 'NumCampaigns',
        responseKey: 'NumCampaigns',
        valueType: 'number',
        defaultOrderDirection: 'desc',
      },
      UpdatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'UpdatedAt',
        label: 'Last Updated',
        orderByKey: 'UpdatedAt',
        requestKey: '',
        responseKey: 'UpdatedAt',
        valueType: 'date',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
    },
  },
  dialogs: {
    bidder: {
      title: 'Associated Bidders',
      dataKey: 'Bidders',
    },
    campaign: {
      title: 'Associated Campaigns',
      dataKey: 'Campaigns',
    },
  },
};
